import React from "react";
import {
  WSTable,
  WSTableCell,
  WSTableColumn,
  WSTableItem,
  WSTableRowMenuAction
} from "@wingspanhq/fe-component-library";
import {
  RequirementAdditionalData,
  RequirementContextType,
  RequirementTableItemData
} from "../types";
import { useRequirementsConfig } from "../useRequirementsConfig";
import { useGeneralRequirementsConfig } from "../modules/GeneralRequirements/useGeneralRequirementsConfig";
import { ExtendedRequirementItem } from "../modules/GeneralRequirements/types";
import {
  IPayeeResponse,
  IPayerResponse,
  IRequirementResponse
} from "@wingspanhq/payments/dist/interfaces";

export type RequirementsTableProps = {
  payerId: string | undefined;
  payeeId: string | undefined;
  payer?: IPayerResponse;
  payee?: IPayeeResponse;
  requirements: Array<ExtendedRequirementItem>;
  contextType: RequirementContextType;
  hideActions?: boolean;
  compact?: boolean;
  showOverrideActions?: boolean;
  requiredAction?: string;
  getOnViewDetails: (
    item: ExtendedRequirementItem,
    additionalData?: RequirementAdditionalData
  ) => ((item: RequirementTableItemData) => void) | undefined;
};

export const ExtendedRequirementsTable: React.FC<RequirementsTableProps> = ({
  payerId,
  payeeId,
  requirements,
  payee,
  payer,
  contextType,
  hideActions,
  compact,
  showOverrideActions,
  getOnViewDetails,
  requiredAction
}) => {
  const generalRequirementsConfig = useGeneralRequirementsConfig({
    payeeId,
    payerId,
    payer,
    payee,
    contextType
  });

  const requirementsConfig = useRequirementsConfig({
    requirements: requirements.filter(
      requirement => "requirementId" in requirement
    ) as RequirementTableItemData[],
    contextType,
    context: {
      showOverrideActions
    }
  });

  const tableData: WSTableItem<ExtendedRequirementItem>[] = requirements.map(
    (requirement, index) => ({
      id:
        "requirementId" in requirement
          ? requirement.requirementId
          : requirement.requirementType + index,
      data: requirement
    })
  );

  const columns: Array<WSTableColumn<ExtendedRequirementItem>> = [
    {
      config: {
        header: {
          text: "Requirement"
        },
        gridTemplateSizeMax: "0.5fr"
      },
      renderFunction: rowItem => {
        if ("requirementId" in rowItem.data) {
          return (
            <WSTableCell
              {...requirementsConfig.getNameCellProps(rowItem.data)}
            />
          );
        } else {
          return (
            <WSTableCell
              {...generalRequirementsConfig.getNameCellProps(rowItem.data)}
            />
          );
        }
      }
    },
    {
      config: {
        header: {
          text: "Type"
        },
        hideOnScreens: compact ? ["XS", "S", "M", "L", "XL"] : ["XS"],
        gridTemplateSizeMax: "0.5fr"
      },
      renderFunction: rowItem => {
        if ("requirementId" in rowItem.data) {
          return (
            <WSTableCell
              {...requirementsConfig.getTypeCellProps(rowItem.data)}
            />
          );
        } else {
          return (
            <WSTableCell
              {...generalRequirementsConfig.getTypeCellProps(rowItem.data)}
            />
          );
        }
      }
    },
    {
      config: {
        header: {
          text: "Engagements"
        },
        gridTemplateSizeMax: "0.4fr"
      },
      renderFunction: rowItem => {
        if ("requirementId" in rowItem.data) {
          return (
            <WSTableCell
              text={
                rowItem.data.payerPayeeEngagementIds?.length?.toString() || "-"
              }
            />
          );
        } else {
          return (
            <WSTableCell
              {...generalRequirementsConfig.getEngagementsCellProps(
                rowItem.data
              )}
            />
          );
        }
      }
    },
    {
      config: {
        header: {
          text: "Status"
        },
        gridTemplateSizeMax: "0.5fr"
      },
      renderFunction: rowItem => {
        if ("requirementId" in rowItem.data) {
          return (
            <WSTableCell
              {...requirementsConfig.getStatusCellProps(rowItem.data)}
            />
          );
        } else {
          return (
            <WSTableCell
              {...generalRequirementsConfig.getStatusCellProps(rowItem.data)}
            />
          );
        }
      }
    }
  ];

  return (
    <WSTable<ExtendedRequirementItem>
      loading={
        requirementsConfig.isLoading || generalRequirementsConfig.isLoading
      }
      key={
        requirementsConfig.dataUpdatedAt ||
        generalRequirementsConfig.dataUpdatedAt
      }
      getOnRowClick={({ data }) => {
        if ("requirementId" in data) {
          const requirement = data as IRequirementResponse;
          const additionalData =
            requirementsConfig.getAdditionalData(requirement);
          const callback = getOnViewDetails(requirement, additionalData);

          return callback
            ? ({ data }) => callback(data as IRequirementResponse)
            : undefined;
        } else {
          return undefined;
        }
      }}
      rowActions={({ data }) => {
        if (hideActions || !requiredAction) {
          return [];
        }

        if ("requirementId" in data) {
          const isActionRequired = requirementsConfig.isActionRequired(data);

          if (isActionRequired) {
            return [
              {
                text: requiredAction,
                onClick: () => {
                  const additionalData =
                    requirementsConfig.getAdditionalData(data);
                  const callback = getOnViewDetails(
                    data as IRequirementResponse,
                    additionalData
                  );

                  return callback && callback(data as IRequirementResponse);
                }
              }
            ];
          }
        }

        return [];
      }}
      rowMenuActions={({ data }) =>
        hideActions
          ? []
          : "requirementId" in data
          ? [
              ...(getOnViewDetails(data)
                ? [
                    {
                      label: "View requirement",
                      icon: "view",
                      onClick: () => {
                        const additionalData =
                          requirementsConfig.getAdditionalData(data);

                        const callback = getOnViewDetails(
                          data as IRequirementResponse,
                          additionalData
                        );

                        return (
                          callback && callback(data as IRequirementResponse)
                        );
                      }
                    } as WSTableRowMenuAction<RequirementTableItemData>
                  ]
                : []),
              ...requirementsConfig.renderRowMenuActions(data)
            ]
          : generalRequirementsConfig.getRowMenuActions(data)
      }
      columns={columns}
      tableData={tableData}
      data-testid="requirementsTable"
    />
  );
};
